import axios from 'axios';

import { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { getAnalytics, logEvent } from "firebase/analytics";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PreviewInfosVertical from '../components/PreviewInfosVertical';
import BottomMenu from '../components/BottomMenu';
import { DeleteOutline, IosShare, Lock, Public } from '@mui/icons-material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Loading from '../components/Loading';
import IconSmall from '../components/IconSmall';
import { navigateToSigninWithRedirect } from '../components/signinUtils';
import { getFilteredTopics } from '../utils/chatUtils';
import { buildMissingRoute } from '../utils/missingRoute';
import { rememberDeletedChat } from '../services/deletedChats';
import {
    CHAT_SHARE_ROUTE,
    clearPendingChatShare,
    hasPendingChatShare,
    setPendingChatShare,
} from '../utils/pendingChatShare';

const SHARE_STATE = {
    OWN_PUBLIC: 'OWN_PUBLIC',
    OWN_PRIVATE: 'OWN_PRIVATE',
    OTHER_PUBLIC: 'OTHER_PUBLIC',
    OTHER_PRIVATE: 'OTHER_PRIVATE'
};
const SHARE_HANDOFF_DIALOG = {
    CONFIRM: 'confirm',
    OWNERSHIP_LOST: 'ownership_lost',
    SIGN_IN: 'sign_in',
};

const Chat = ({
    authenticated,
    uid
}) => {
    let params = useParams();
    const location = useLocation();
    const analytics = getAnalytics();
    const navigate = useNavigate();
    const missingRoute = buildMissingRoute(location);

    const [chatData, setChatData] = useState(null);
    const [popupMessage, setPopupMessage] = useState('');
    const [deletingChat, setDeletingChat] = useState(false);
    const [shareHandoffDialog, setShareHandoffDialog] = useState(null);
    const pendingShareConfirmationHandledRef = useRef(false);

    const audio = document.getElementById('audio');

    useEffect(() => {
        logEvent(analytics, "screen_view", { firebase_screen: "chat" });
        audio.pause();

        const returningFromShareSignin = hasPendingChatShare({
            threadId: params.chatId,
            route: CHAT_SHARE_ROUTE.CHAT,
        });

        if (returningFromShareSignin && !authenticated) {
            return;
        }

        if (location.state?.chatData) {
            setChatData(location.state.chatData);
            return;
        }

        if (!!params.chatId) {
            const request = authenticated
                ? axios.post(`/chat/${params.chatId}`)
                : axios.get(`/chat/${params.chatId}`);

            request
                .then((res) => {
                    const data = res.data;
                    setChatData(data);
                })
                .catch((err) => {
                    console.log(err);
                    const status = err && err.response ? err.response.status : null;

                    if (returningFromShareSignin && (status === 401 || status === 403 || status === 404)) {
                        clearPendingChatShare();
                        setShareHandoffDialog(SHARE_HANDOFF_DIALOG.OWNERSHIP_LOST);
                        return;
                    }

                    if (returningFromShareSignin) {
                        setPopupMessage('Unable to load this conversation. Please try again.');
                        return;
                    }

                    navigate(missingRoute);
                });
        } else {
            navigate(missingRoute);
        }
    }, [params.chatId, location.state, missingRoute, navigate, uid, authenticated]);

    const getChatResponse = (chatData, chatId) => {
        if (!chatData || !chatData.previews || chatData.previews.length === 0) {
            return "I couldn't find a relevant podcast highlight for this yet.";
        }

        if (!chatData.topics || chatData.topics.length === 0) {
            return "Hear people explore this topic in these podcast highlights.";
        }

        const introPhrases = [
            "Hear people discuss",
            "Hear podcast guests talk about",
            "Listen to human perspectives on",
            "Hear podcast guests explore",
            "Meet the voices discussing",
        ];

        const seedString = chatId || chatData.chatId || '';
        const seedValue = seedString
            .split('')
            .reduce((accumulator, character) => accumulator + character.charCodeAt(0), 0);
        const introPhrase = introPhrases[seedValue % introPhrases.length];

        const filteredTopics = getFilteredTopics(chatData.topics);
        if (filteredTopics.length === 0) {
            return "I couldn't find a relevant podcast highlight for this yet.";
        }

        const displayedTopics = filteredTopics.slice(0, 2);
        return (
            <>
                {introPhrase}{' '}
                <span className='chatTopic'>{displayedTopics[0]}</span>
                {displayedTopics.length > 1 && (
                    <>
                        {' '}and <span className='chatTopic'>{displayedTopics[1]}</span>
                    </>
                )}
                .
            </>
        );
    }

    const getShareState = () => {
        if (!chatData) return null;

        const chatOwnerId = chatData.uid;
        const isOwner = !!uid && !!chatOwnerId && uid === chatOwnerId;
        const isPublic = !!chatData.isPublic;

        if (isOwner && isPublic) return SHARE_STATE.OWN_PUBLIC;
        if (isOwner && !isPublic) return SHARE_STATE.OWN_PRIVATE;
        if (!isOwner && isPublic) return SHARE_STATE.OTHER_PUBLIC;
        return SHARE_STATE.OTHER_PRIVATE;
    }

    useEffect(() => {
        pendingShareConfirmationHandledRef.current = false;
    }, [params.chatId]);

    useEffect(() => {
        if (
            !authenticated ||
            !chatData ||
            chatData.chatId !== params.chatId ||
            pendingShareConfirmationHandledRef.current ||
            !hasPendingChatShare({
                threadId: params.chatId,
                route: CHAT_SHARE_ROUTE.CHAT,
            })
        ) {
            return;
        }

        const isOwner = Boolean(uid && chatData.uid && uid === chatData.uid);

        if (!isOwner) {
            clearPendingChatShare();
            setShareHandoffDialog(SHARE_HANDOFF_DIALOG.OWNERSHIP_LOST);
            return;
        }

        setShareHandoffDialog(SHARE_HANDOFF_DIALOG.CONFIRM);
    }, [authenticated, chatData, params.chatId, uid]);

    const changePublicPrivate = async () => {
        const shareState = getShareState();
        // if it's other person's chat, show a popup that says that only the owner can change the setting.
        if (shareState === SHARE_STATE.OTHER_PUBLIC) {
            setPopupMessage("Only the owner can change this chat’s privacy setting.");
            return false;
        }

        // check if user is logged in, if not display a popup that says you must be logged in to change the setting.
        if (!uid) {
            setPopupMessage("You must be logged in to change the privacy setting.");
            return false;
        }

        if (!chatData) {
            return false;
        }

        const newIsPublic = chatData.isPublic ? false : true;
        // Update the UI immediately for a snappier feel, then revert if the request fails.
        setChatData((prevChatData) => ({
            ...prevChatData,
            isPublic: newIsPublic
        }));

        try {
            const res = await axios.post('/chat_set_public', {
                chatId: chatData.chatId,
                isPublic: newIsPublic
            });

            if (res.status !== 200) {
                setChatData((prevChatData) => ({
                    ...prevChatData,
                    isPublic: !newIsPublic
                }));
                setPopupMessage("An error occurred while changing the privacy setting. Please try again.");
                return false;
            }

            return true;
        } catch (err) {
            setChatData((prevChatData) => ({
                ...prevChatData,
                isPublic: !newIsPublic
            }));

            const status = err && err.response ? err.response.status : null;
            if (
                hasPendingChatShare({
                    threadId: params.chatId,
                    route: CHAT_SHARE_ROUTE.CHAT,
                }) &&
                (status === 401 || status === 403 || status === 404)
            ) {
                clearPendingChatShare();
                setShareHandoffDialog(SHARE_HANDOFF_DIALOG.OWNERSHIP_LOST);
                return false;
            }

            setPopupMessage("An error occurred while changing the privacy setting. Please try again.");
            return false;
        }
    }

    const shareLinkClick = async () => {
        const shareState = getShareState();

        if (!authenticated && chatData && !chatData.isPublic) {
            setShareHandoffDialog(SHARE_HANDOFF_DIALOG.SIGN_IN);
            return;
        }

        const wasPrivateBefore = uid && shareState === SHARE_STATE.OWN_PRIVATE;

        if (wasPrivateBefore) {
            const updatedToPublic = await changePublicPrivate();
            if (!updatedToPublic) {
                return;
            }
        } else if (shareState === SHARE_STATE.OTHER_PRIVATE) {
            setPopupMessage("This chat is private and cannot be shared.");
            return;
        }

        if (!chatData) {
            return;
        }

        if (hasPendingChatShare({
            threadId: params.chatId,
            route: CHAT_SHARE_ROUTE.CHAT,
        })) {
            clearPendingChatShare();
        }

        const shareUrl = `${window.location.origin}/chat/${chatData.chatId}`;
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                if (wasPrivateBefore) {
                    setPopupMessage("This chat is now publicly accessible. Link copied to clipboard!");
                } else {
                    setPopupMessage("Link copied to clipboard!");
                }
            })
            .catch((err) => {
                console.log(err);
                setPopupMessage("Failed to copy the link. Please try again.");
            });
    }

    const continueToShareSignin = () => {
        const pendingShare = setPendingChatShare({
            threadId: params.chatId,
            route: CHAT_SHARE_ROUTE.CHAT,
        });

        if (!pendingShare) {
            setShareHandoffDialog(null);
            setPopupMessage('Unable to continue to sign in. Please try again.');
            return;
        }

        setShareHandoffDialog(null);
        navigateToSigninWithRedirect(navigate, pendingShare.path);
    }

    const dismissShareHandoff = () => {
        if (shareHandoffDialog === SHARE_HANDOFF_DIALOG.CONFIRM) {
            clearPendingChatShare();
        }

        setShareHandoffDialog(null);
    }

    const confirmPendingShare = () => {
        pendingShareConfirmationHandledRef.current = true;
        setShareHandoffDialog(null);
        void shareLinkClick();
    }

    const leaveOwnershipLostChat = () => {
        clearPendingChatShare();
        setShareHandoffDialog(null);
        navigate('/home', { replace: true });
    }

    const deleteChatClick = async () => {
        if (!chatData || deletingChat) {
            return;
        }

        const shareState = getShareState();
        const isOwner = shareState === SHARE_STATE.OWN_PUBLIC || shareState === SHARE_STATE.OWN_PRIVATE;
        if (!authenticated || !isOwner) {
            setPopupMessage('Only the owner can delete this chat.');
            return;
        }

        setDeletingChat(true);
        try {
            const res = await axios.post('/chat_delete', { "chatId": chatData.chatId });
            if (res.status === 200 && !res.data?.error) {
                rememberDeletedChat(uid, `1:${chatData.chatId}`);
                navigate('/home');
                return;
            }
            setPopupMessage('There was an error deleting this chat.');
        } catch (err) {
            setPopupMessage('There was an error deleting this chat.');
        } finally {
            setDeletingChat(false);
        }
    }

    return (
        <div className="container">
            <div className="hasBottomMenu">
                {chatData ?
                    <div>
                        {(() => {
                            const shareState = getShareState();
                            const isPublicState = shareState === SHARE_STATE.OWN_PUBLIC || shareState === SHARE_STATE.OTHER_PUBLIC;
                            return (
                                <div style={{ padding: '20px 20px 10px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div style={{ flexGrow: 1, textAlign: 'left' }}>
                                            <button onClick={changePublicPrivate}>
                                                <div>
                                                    <span style={{ marginRight: '6px' }}>
                                                        {isPublicState ?
                                                            <Public style={{ fontSize: '18px', verticalAlign: 'middle' }} />
                                                            :
                                                            <Lock style={{ fontSize: '18px', verticalAlign: 'middle' }} />
                                                        }
                                                    </span>
                                                    <span style={{ fontSize: '14px', fontWeight: '500', verticalAlign: 'middle' }}>{isPublicState ? 'Public' : 'Only visible to you'}</span>
                                                </div>
                                            </button>
                                        </div>
                                        <div>
                                            <button onClick={shareLinkClick} className='shareChat blurBackground' style={{ width: 'auto', padding: '6px 16px', borderRadius: '20px' }}>
                                                <span style={{ marginRight: '6px' }}><IosShare style={{ fontSize: '16px', verticalAlign: 'middle' }} /></span>
                                                <span style={{ verticalAlign: 'middle' }}>Share</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        <div className='chatQuery chatBoxBackground chatBoxGlowStatic'>
                            <div className='chatQueryText'>
                                {chatData.query}
                            </div>
                            
                        </div>

                        {(() => {
                            const shareState = getShareState();
                            const isOwner = shareState === SHARE_STATE.OWN_PUBLIC || shareState === SHARE_STATE.OWN_PRIVATE;

                            if (!authenticated || !isOwner) {
                                return null;
                            }

                            return (
                                <div style={{ margin: '6px 20px 10px', textAlign: 'right' }}>
                                    <button onClick={deleteChatClick} disabled={deletingChat} style={{ fontWeight: '400', fontSize: '14px', color: 'var(--app-text-secondary)' }}>
                                        <span style={{ marginRight: '4px', verticalAlign: 'middle' }}>
                                            <DeleteOutline style={{ fontSize: '18px', verticalAlign: 'middle' }} />
                                        </span>
                                        <span style={{ verticalAlign: 'middle' }}>
                                            {deletingChat ? 'deleting...' : 'delete'}
                                        </span>
                                    </button>
                                </div>
                            );
                        })()}

                        <div>
                            <div style={{ margin: '0px 20px' }}>
                                <IconSmall />
                            </div>
                            <div className='chatResponse'>{getChatResponse(chatData, params.chatId)}</div>
                            <PreviewInfosVertical
                                queueChatResponse
                                title="Highlights"
                                previews={chatData.previews}
                                previewInfoProps={{ showSummary: false }}
                            />
                        </div>
                    </div>
                    :
                    <div style={{ padding: '40px', textAlign: "center" }}>
                        <Loading />
                    </div>
                }

                <Dialog
                    open={!!shareHandoffDialog}
                    onClose={shareHandoffDialog === SHARE_HANDOFF_DIALOG.OWNERSHIP_LOST ?
                        leaveOwnershipLostChat :
                        dismissShareHandoff}
                >
                    <DialogTitle>
                        {shareHandoffDialog === SHARE_HANDOFF_DIALOG.SIGN_IN && 'Sign in to share'}
                        {shareHandoffDialog === SHARE_HANDOFF_DIALOG.CONFIRM && 'Share this conversation?'}
                        {shareHandoffDialog === SHARE_HANDOFF_DIALOG.OWNERSHIP_LOST && 'Unable to share'}
                    </DialogTitle>
                    <DialogContent>
                        {shareHandoffDialog === SHARE_HANDOFF_DIALOG.SIGN_IN &&
                            'Sign in to create a shareable link for this conversation.'}
                        {shareHandoffDialog === SHARE_HANDOFF_DIALOG.CONFIRM &&
                            'Anyone with the link will be able to view it.'}
                        {shareHandoffDialog === SHARE_HANDOFF_DIALOG.OWNERSHIP_LOST &&
                            'This conversation can’t be shared because it is no longer associated with your account.'}
                    </DialogContent>
                    <DialogActions>
                        {shareHandoffDialog === SHARE_HANDOFF_DIALOG.SIGN_IN && (
                            <>
                                <button onClick={dismissShareHandoff} style={{ padding: '8px 14px' }}>
                                    Cancel
                                </button>
                                <button onClick={continueToShareSignin} style={{ padding: '8px 14px' }}>
                                    Sign in
                                </button>
                            </>
                        )}
                        {shareHandoffDialog === SHARE_HANDOFF_DIALOG.CONFIRM && (
                            <>
                                <button onClick={dismissShareHandoff} style={{ padding: '8px 14px' }}>
                                    Cancel
                                </button>
                                <button onClick={confirmPendingShare} style={{ padding: '8px 14px' }}>
                                    Create link
                                </button>
                            </>
                        )}
                        {shareHandoffDialog === SHARE_HANDOFF_DIALOG.OWNERSHIP_LOST && (
                            <button onClick={leaveOwnershipLostChat} style={{ padding: '8px 14px' }}>
                                Home
                            </button>
                        )}
                    </DialogActions>
                </Dialog>

                <Dialog open={!!popupMessage} onClose={() => setPopupMessage('')}>
                    <DialogContent>
                        {popupMessage}
                    </DialogContent>
                    <DialogActions>
                        <button onClick={() => setPopupMessage('')} style={{ padding: '8px 14px' }}>
                            OK
                        </button>
                    </DialogActions>
                </Dialog>

                <BottomMenu />
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid
});

export default connect(mapStateToProps, {})(Chat);
